import Cookie from 'js-cookie';

import {
    updateSponsorApi,
    updateSponsorSettingsApi,
    getSponsor,
    updateThirdPartyPlatformInfo
} from 'api/sponsor';
import generateUpdatePayload from 'utils/generateUpdatePayload';
import { replaceAvailablePlanItems } from 'store/module/availablePlanItems';
import { setFailed } from 'store/module/flag';
import { cookieKeys } from 'config/cookie';
import { isNull } from 'lodash';

const INIT_SPONSOR = 'sponsor/INIT_SPONSOR';
const UPDATE_SPONSOR = 'sponsor/UPDATE_SPONSOR';
export const CHANGE_SPONSOR = 'sponsor/CHANGE_SPONSOR';
const defaultState = {
    settings: {},
    thirdParty: {},
    event: { settings: {} },
    channel: { settings: {} }
};

/**
 * 合併設定資料，處理巢狀的 settings 物件
 * @param {Object} state - 當前的 state 物件
 * @param {Object} payload - 要合併的新資料
 * @param {string} mainKey - 主要的鍵值，例如 'thirdParty', 'event', 'channel'
 * @returns {Object} - 合併後的物件
 */
const mergeSettings = (state, payload, mainKey) => {
    const res = {
        ...state?.[mainKey],
        ...(Boolean(payload?.[mainKey])
            ? Object.keys(payload?.[mainKey] || {}).reduce((acc, infoKey) => {
                  if (infoKey === 'settings') {
                      acc[infoKey] = {
                          ...(state?.[mainKey]?.settings || {}),
                          ...(Boolean(payload?.[mainKey]?.settings)
                              ? Object.keys(payload?.[mainKey]?.settings || {}).reduce(
                                    (acc, settingKey) => {
                                        acc[settingKey] =
                                            payload?.[mainKey]?.settings[settingKey] ||
                                            state?.[mainKey]?.settings[settingKey];

                                        return acc;
                                    },
                                    {}
                                )
                              : {})
                      };
                  } else {
                      acc[infoKey] = payload?.[mainKey]?.[infoKey] || state?.[mainKey]?.[infoKey];
                  }
                  return acc;
              }, {})
            : {})
    };

    return res;
};

export default function reducer(state = defaultState, { type, payload }) {
    switch (type) {
        case UPDATE_SPONSOR:
            return {
                ...state,
                ...payload,
                settings: { ...state.settings, ...payload.settings },
                thirdParty: mergeSettings(state, payload, 'thirdParty')
            };
        case CHANGE_SPONSOR:
        case INIT_SPONSOR:
            return { ...payload };
        default:
            return state;
    }
}

export function initSponsor(data) {
    return {
        type: INIT_SPONSOR,
        payload: data
    };
}

export function updateSponsorDataWithNoFetch(data) {
    return {
        type: UPDATE_SPONSOR,
        payload: data
    };
}

export function changeSponsor(sponsorId) {
    return async (dispatch, getState) => {
        const { sponsor: { id } = {} } = getState();
        if (sponsorId === id) {
            return;
        }
        try {
            const sponsorData = await getSponsor(sponsorId);
            dispatch(replaceAvailablePlanItems(sponsorData.availablePlanItems));
            dispatch({
                type: CHANGE_SPONSOR,
                payload: sponsorData.sponsor
            });
            Cookie.set(cookieKeys.LATEST_SELECTED_SPONSOR_ID, sponsorId, {
                expires: 7
            });
            return sponsorData.sponsor;
        } catch (error) {
            console.error(error);
            dispatch(setFailed('ChangeSponsor', 'message_connection_error'));
        }
    };
}

export function updateSponsorData(data) {
    return async (dispatch, getState) => {
        const {
            sponsor: { id, settings: settingOriginData, thirdParty: thirdPartyOriginData = {} } = {}
        } = getState();
        const { settings, event, channel, thirdParty, ...sponsorInfoData } = data;
        let promises = [];
        if (Object.keys(sponsorInfoData).length !== 0) {
            promises.push(updateSponsorApi(id, sponsorInfoData));
        }

        if (settings) {
            const originSameSettings = getSameObjectKey(settingOriginData, settings);
            const removeEmptySettings = removeEmptyObject(settings);
            const addRemoveSettings = generateUpdatePayload(
                originSameSettings,
                removeEmptySettings
            );
            // 如果全部為 undefined 代表沒有更新。 PS. 有要清除的出現 deleted 陣列
            if (Object.values(addRemoveSettings).some(v => v !== undefined)) {
                promises.push(updateSponsorSettingsApi(id, addRemoveSettings));
            }
        }

        if (thirdParty) {
            Object.entries(thirdParty).forEach(([platform, data]) => {
                if (thirdPartyOriginData[platform]) {
                    const originPlatform = thirdPartyOriginData[platform];
                    const diffPayload = Object.entries(data).reduce((acc, [key, value]) => {
                        if (
                            originPlatform[key] === value ||
                            (isNull(originPlatform[key]) && isNull(value))
                        )
                            return acc;
                        if (
                            value === undefined ||
                            value === null ||
                            value === '' ||
                            (Array.isArray(value) && value.length === 0) ||
                            (typeof value === 'object' && Object.keys(value).length === 0)
                        ) {
                            // Third Party 沒有 deleted 的概念，需直接轉成空字串
                            return { ...acc, [key]: '' };
                        }
                        return { ...acc, [key]: value };
                    }, {});

                    if (Object.keys(diffPayload).length !== 0) {
                        promises.push(
                            updateThirdPartyPlatformInfo({
                                sponsorId: id,
                                platform,
                                settings: diffPayload
                            })
                        );
                    }
                } else {
                    promises.push(
                        updateThirdPartyPlatformInfo({ sponsorId: id, platform, settings: data })
                    );
                }
            });
        }

        await Promise.all(promises);

        dispatch({
            type: UPDATE_SPONSOR,
            payload: data
        });
    };
}

export function getSameObjectKey(origin, target) {
    return Object.keys(target).reduce((object, k) => {
        object[k] = origin[k];
        return object;
    }, {});
}

export function removeEmptyObject(obj) {
    return Object.keys(obj).reduce((object, k) => {
        if (obj[k] !== '') {
            object[k] = obj[k];
        }
        return object;
    }, {});
}
